/**
 * FERP Portal - Task Group Progress Page
 *
 * Standalone page at /task-groups/:groupId/progress showing completion
 * status of a task group's child tasks across staff members.
 *
 * Domain-agnostic: works for any task group in any industry --
 * competency areas, verification methods, and role scoping are
 * all rendered generically from backend data.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend queries
 * - No hardcoded field values -- displays what backend returns
 * - Domain-agnostic: no "Resident", "CNA", "144G", etc.
 * - Apollo hooks from @apollo/client/react
 * - gql from @apollo/client
 */

import { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useTaskGroupDetail } from '@/hooks/useTaskGroups';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  Breadcrumbs,
  Anchor,
  SimpleGrid,
  Tooltip,
  Select,
  Modal,
  Divider,
  Progress,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChartBar,
  IconCheck,
  IconClock,
  IconMinus,
  IconUsers,
  IconListCheck,
  IconPercentage,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { GqlTaskStatus, useListActorsQuery } from '@/generated/graphql';

import type { TaskGroupDetail } from '@/hooks/useTaskGroups';
type ChildTask = TaskGroupDetail['children'][number];

// ============================================================================
// CUSTOM QUERY — includes taskGroupRef + assignedActor for progress tracking
// ============================================================================

const LIST_TASKS_WITH_GROUP_REF = gql`
  query ListTasksWithGroupRef($limit: Int, $cursor: String) {
    taskApiListTasks(limit: $limit, cursor: $cursor) {
      tasks {
        id
        name
        status
        completedAt
        startedAt
        mandateRef
        createdBySource
        taskGroupRef
        assignedActor {
          id
          displayName
        }
        metadata
      }
      hasMore
      nextCursor
    }
  }
`;

interface TaskWithGroupRef {
  id: string;
  name: string;
  status: GqlTaskStatus;
  completedAt?: string | null;
  startedAt?: string | null;
  mandateRef?: string | null;
  createdBySource: string;
  taskGroupRef?: string | null;
  assignedActor?: { id: string; displayName: string } | null;
  metadata: Record<string, unknown>;
}

interface ListTasksWithGroupRefData {
  taskApiListTasks: {
    tasks: TaskWithGroupRef[];
    hasMore: boolean;
    nextCursor?: string | null;
  };
}

// ============================================================================
// TYPES
// ============================================================================

type CellStatus = 'completed' | 'in_progress' | 'not_started';

interface CellDetail {
  staffName: string;
  taskName: string;
  status: CellStatus;
  completedAt?: string | null;
  startedAt?: string | null;
  mandateRef?: string | null;
  createdBySource?: string;
  metadata?: Record<string, unknown>;
}

interface StaffRow {
  actorId: string;
  actorName: string;
  actorRole: string;
  cells: Map<string, CellStatus>;
  taskDetails: Map<string, TaskWithGroupRef>;
  completedCount: number;
  applicableCount: number;
}

// ============================================================================
// HELPERS — all generic, no domain knowledge
// ============================================================================

/** Format a snake_case key to Title Case for display */
function formatLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Derive cell status from task status enum */
function toCellStatus(status: GqlTaskStatus): CellStatus {
  if (status === GqlTaskStatus.COMPLETED) return 'completed';
  if (
    status === GqlTaskStatus.IN_PROGRESS ||
    status === GqlTaskStatus.ASSIGNED
  ) {
    return 'in_progress';
  }
  return 'not_started';
}

/** Get color for a cell status */
function cellColor(status: CellStatus): string {
  switch (status) {
    case 'completed':
      return 'green';
    case 'in_progress':
      return 'yellow';
    case 'not_started':
      return 'gray';
  }
}

/** Cell icon for status */
function CellIcon({ status }: { status: CellStatus }) {
  switch (status) {
    case 'completed':
      return <IconCheck size={14} />;
    case 'in_progress':
      return <IconClock size={14} />;
    case 'not_started':
      return <IconMinus size={14} />;
  }
}

/** Check if a child task's requiredRole list includes the given actor role */
function isChildRequiredForRole(child: ChildTask, actorRole: string): boolean {
  if (!child.requiredRole || child.requiredRole.length === 0) return true;
  return child.requiredRole.includes(actorRole);
}

/** Derive unique roles from all children's requiredRole arrays */
function deriveRoles(children: ChildTask[]): string[] {
  const roles = new Set<string>();
  for (const child of children) {
    if (child.requiredRole) {
      for (const r of child.requiredRole) roles.add(r);
    }
  }
  return Array.from(roles).sort();
}

/** Derive unique competency areas from children */
function deriveAreas(children: ChildTask[]): string[] {
  const areas = new Set<string>();
  for (const child of children) {
    if (child.competencyArea) areas.add(child.competencyArea);
  }
  return Array.from(areas).sort();
}

/** Format ISO date to short display */
function formatDate(iso?: string | null): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ============================================================================
// CELL DETAIL MODAL
// ============================================================================

function CellDetailModal({
  detail,
  opened,
  onClose,
}: {
  detail: CellDetail | null;
  opened: boolean;
  onClose: () => void;
}) {
  if (!detail) return null;

  return (
    <Modal opened={opened} onClose={onClose} title="Task Detail" size="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Staff</Text>
          <Text size="sm" fw={500}>{detail.staffName}</Text>
        </Group>
        <Divider />
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Task</Text>
          <Text size="sm" fw={500}>{detail.taskName}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">Status</Text>
          <Badge color={cellColor(detail.status)} variant="filled" size="sm">
            {formatLabel(detail.status)}
          </Badge>
        </Group>
        {detail.completedAt && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Completed</Text>
            <Text size="sm">{formatDate(detail.completedAt)}</Text>
          </Group>
        )}
        {detail.startedAt && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Started</Text>
            <Text size="sm">{formatDate(detail.startedAt)}</Text>
          </Group>
        )}
        {detail.mandateRef && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Mandate Reference</Text>
            <Badge size="sm" variant="light" color="blue">{detail.mandateRef}</Badge>
          </Group>
        )}
        {detail.createdBySource && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Created By</Text>
            <Text size="sm">{formatLabel(detail.createdBySource)}</Text>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TaskGroupProgressPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  // Filters
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Cell detail modal
  const [cellDetail, setCellDetail] = useState<CellDetail | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  // Queries
  const { taskGroup: group, loading: groupLoading, error: groupError } = useTaskGroupDetail(
    groupId ?? '',
  );

  const { data: actorsData, loading: actorsLoading } = useListActorsQuery({
    variables: { limit: 200 },
    skip,
  });

  const { data: tasksData, loading: tasksLoading } = useQuery<ListTasksWithGroupRefData>(
    LIST_TASKS_WITH_GROUP_REF,
    {
      variables: { limit: 1000 },
      skip,
    },
  );

  // Derived data
  const allRoles = useMemo(() => (group ? deriveRoles(group.children) : []), [group]);
  const allAreas = useMemo(() => (group ? deriveAreas(group.children) : []), [group]);

  // Filter children by competency area
  const filteredChildren = useMemo(() => {
    if (!group) return [];
    let children = group.children;
    if (areaFilter) {
      children = children.filter((c) => c.competencyArea === areaFilter);
    }
    return children;
  }, [group, areaFilter]);

  // Tasks for this task group
  const groupTasks = useMemo(() => {
    if (!tasksData || !group) return [];
    return tasksData.taskApiListTasks.items.filter(
      (t) => t.taskGroupRef === group.configId,
    );
  }, [tasksData, group]);

  // Build staff rows
  const staffRows = useMemo((): StaffRow[] => {
    const actors = actorsData?.listActors?.items ?? [];

    // Build actor → task map
    const actorTaskMap = new Map<string, TaskWithGroupRef[]>();
    for (const task of groupTasks) {
      const actorId = task.assignedActor?.id ?? '';
      if (!actorId) continue;
      if (!actorTaskMap.has(actorId)) actorTaskMap.set(actorId, []);
      actorTaskMap.get(actorId)!.push(task);
    }

    // Build rows from actors that have tasks OR are in the actors list
    const actorIds = new Set<string>([
      ...actorTaskMap.keys(),
      ...actors.map((a) => a.id),
    ]);

    const rows: StaffRow[] = [];
    for (const actorId of actorIds) {
      const actor = actors.find((a) => a.id === actorId);
      const actorRole = actor?.actorType?.toLowerCase() ?? '';
      const actorName = actor?.name ?? actorId;
      const actorTasks = actorTaskMap.get(actorId) ?? [];

      // Filter by role
      if (roleFilter && actorRole !== roleFilter) continue;

      const cells = new Map<string, CellStatus>();
      const taskDetails = new Map<string, TaskWithGroupRef>();
      let completedCount = 0;
      let applicableCount = 0;

      for (const child of filteredChildren) {
        if (!isChildRequiredForRole(child, actorRole)) continue;
        applicableCount++;

        // Find matching task
        const matchingTask = actorTasks.find((t) => t.name === child.name);
        if (matchingTask) {
          const status = toCellStatus(matchingTask.status);
          cells.set(child.id, status);
          taskDetails.set(child.id, matchingTask);
          if (status === 'completed') completedCount++;
        } else {
          cells.set(child.id, 'not_started');
        }
      }

      // Filter by status
      if (statusFilter === 'completed' && completedCount !== applicableCount) continue;
      if (statusFilter === 'incomplete' && completedCount === applicableCount) continue;

      // Skip actors with zero applicable children
      if (applicableCount === 0) continue;

      rows.push({
        actorId,
        actorName,
        actorRole,
        cells,
        taskDetails,
        completedCount,
        applicableCount,
      });
    }

    return rows.sort((a, b) => a.actorName.localeCompare(b.actorName));
  }, [actorsData, groupTasks, filteredChildren, roleFilter, statusFilter]);

  // Summary stats
  const stats = useMemo(() => {
    const totalModules = filteredChildren.length;
    const totalCompleted = staffRows.reduce((sum, r) => sum + r.completedCount, 0);
    const totalApplicable = staffRows.reduce((sum, r) => sum + r.applicableCount, 0);
    const coveragePct = totalApplicable > 0 ? Math.round((totalCompleted / totalApplicable) * 100) : 0;
    return { totalModules, totalCompleted, totalApplicable, coveragePct };
  }, [filteredChildren, staffRows]);

  const handleCellClick = useCallback(
    (row: StaffRow, child: ChildTask) => {
      const status = row.cells.get(child.id) ?? 'not_started';
      const task = row.taskDetails.get(child.id);
      setCellDetail({
        staffName: row.actorName,
        taskName: child.name,
        status,
        completedAt: task?.completedAt,
        startedAt: task?.startedAt,
        mandateRef: task?.mandateRef,
        createdBySource: task?.createdBySource,
        metadata: task?.metadata,
      });
      setModalOpened(true);
    },
    [],
  );

  // Loading state
  const isLoading = groupLoading || actorsLoading || tasksLoading;

  if (groupError) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Task Group">
          {groupError.message}
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
          <Text c="dimmed">Loading progress data...</Text>
        </Group>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Not Found">
          Task group <strong>{groupId}</strong> was not found.
        </Alert>
        <Button
          mt="md"
          leftSection={<IconArrowLeft size={16} />}
          variant="light"
          onClick={() => navigate('/ferp-core/task-groups')}
        >
          Back to Task Groups
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/ferp-core/task-groups')} style={{ cursor: 'pointer' }}>
            Task Groups
          </Anchor>
          <Anchor
            onClick={() => navigate(`/ferp-core/task-groups/${groupId}`)}
            style={{ cursor: 'pointer' }}
          >
            {group.name}
          </Anchor>
          <Text>Progress</Text>
        </Breadcrumbs>

        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>{group.name} -- Progress</Title>
            <Text c="dimmed" size="sm">
              Completion status across all assigned staff
            </Text>
          </div>
          <Button
            leftSection={<IconArrowLeft size={16} />}
            variant="light"
            onClick={() => navigate(`/ferp-core/task-groups/${groupId}`)}
          >
            Back to Detail
          </Button>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder p="md">
            <Group>
              <ThemeIcon variant="light" color="blue" size="lg">
                <IconListCheck size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Modules</Text>
                <Text fw={700} size="xl">{stats.totalModules}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group>
              <ThemeIcon variant="light" color="green" size="lg">
                <IconCheck size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Completed (all staff)</Text>
                <Text fw={700} size="xl">
                  {stats.totalCompleted} / {stats.totalApplicable}
                </Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group>
              <ThemeIcon
                variant="light"
                color={stats.coveragePct >= 80 ? 'green' : stats.coveragePct >= 50 ? 'yellow' : 'red'}
                size="lg"
              >
                <IconPercentage size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Coverage</Text>
                <Group gap="xs" align="baseline">
                  <Text fw={700} size="xl">{stats.coveragePct}%</Text>
                  <Progress
                    value={stats.coveragePct}
                    size="sm"
                    color={stats.coveragePct >= 80 ? 'green' : stats.coveragePct >= 50 ? 'yellow' : 'red'}
                    style={{ width: 80 }}
                  />
                </Group>
              </div>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Paper withBorder p="md">
          <Group gap="md">
            <Text size="sm" fw={500}>Filters:</Text>
            {allRoles.length > 0 && (
              <Select
                size="sm"
                placeholder="All roles"
                clearable
                data={allRoles.map((r) => ({ value: r, label: formatLabel(r) }))}
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ minWidth: 160 }}
              />
            )}
            {allAreas.length > 0 && (
              <Select
                size="sm"
                placeholder="All areas"
                clearable
                data={allAreas.map((a) => ({ value: a, label: formatLabel(a) }))}
                value={areaFilter}
                onChange={setAreaFilter}
                style={{ minWidth: 160 }}
              />
            )}
            <Select
              size="sm"
              placeholder="All statuses"
              clearable
              data={[
                { value: 'completed', label: 'Completed' },
                { value: 'incomplete', label: 'Incomplete' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ minWidth: 160 }}
            />
            {(roleFilter || areaFilter || statusFilter) && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setRoleFilter(null);
                  setAreaFilter(null);
                  setStatusFilter(null);
                }}
              >
                Clear All
              </Button>
            )}
          </Group>
        </Paper>

        {/* Heatmap Grid */}
        {staffRows.length === 0 ? (
          <Alert icon={<IconInfoCircle size={16} />} color="gray" title="No Data">
            No staff data matches the current filters. Adjust filters or ensure tasks are assigned.
          </Alert>
        ) : (
          <Paper withBorder p="md" style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 150, position: 'sticky', left: 0, background: 'var(--mantine-color-body)', zIndex: 1 }}>
                    Staff
                  </Table.Th>
                  <Table.Th style={{ minWidth: 70 }}>Role</Table.Th>
                  <Table.Th style={{ minWidth: 100 }}>Progress</Table.Th>
                  {filteredChildren.map((child) => (
                    <Table.Th
                      key={child.id}
                      style={{ textAlign: 'center', minWidth: 40, padding: '4px 2px' }}
                    >
                      <Tooltip
                        label={`${child.name}${child.competencyArea ? ` (${formatLabel(child.competencyArea)})` : ''}${child.verification ? ` -- Verification: ${formatLabel(child.verification)}` : ''}`}
                        multiline
                        w={250}
                      >
                        <Text
                          size="xs"
                          fw={600}
                          style={{
                            writingMode: 'vertical-lr',
                            transform: 'rotate(180deg)',
                            maxHeight: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {child.name}
                        </Text>
                      </Tooltip>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {staffRows.map((row) => (
                  <Table.Tr key={row.actorId}>
                    <Table.Td
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: 'var(--mantine-color-body)',
                        zIndex: 1,
                      }}
                    >
                      <Text size="sm" fw={500}>{row.actorName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light">{row.actorRole || '--'}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Progress
                          value={
                            row.applicableCount > 0
                              ? (row.completedCount / row.applicableCount) * 100
                              : 0
                          }
                          size="sm"
                          color={
                            row.completedCount === row.applicableCount ? 'green' : 'orange'
                          }
                          style={{ flex: 1, minWidth: 50 }}
                        />
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {row.completedCount}/{row.applicableCount}
                        </Text>
                      </Group>
                    </Table.Td>
                    {filteredChildren.map((child) => {
                      const applicable = isChildRequiredForRole(child, row.actorRole);
                      if (!applicable) {
                        return (
                          <Table.Td key={child.id} style={{ textAlign: 'center' }}>
                            <Tooltip label="Not required for this role">
                              <Badge variant="light" color="gray" size="sm">
                                <IconMinus size={12} />
                              </Badge>
                            </Tooltip>
                          </Table.Td>
                        );
                      }
                      const status = row.cells.get(child.id) ?? 'not_started';
                      return (
                        <Table.Td
                          key={child.id}
                          style={{ textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleCellClick(row, child)}
                        >
                          <Tooltip label={`${child.name}: ${formatLabel(status)}`}>
                            <Badge
                              variant="filled"
                              color={cellColor(status)}
                              size="sm"
                              style={{ minWidth: 28 }}
                            >
                              <CellIcon status={status} />
                            </Badge>
                          </Tooltip>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        {/* Legend */}
        <Group gap="lg">
          <Group gap="xs">
            <Badge variant="filled" color="green" size="sm">
              <IconCheck size={12} />
            </Badge>
            <Text size="xs" c="dimmed">Completed</Text>
          </Group>
          <Group gap="xs">
            <Badge variant="filled" color="yellow" size="sm">
              <IconClock size={12} />
            </Badge>
            <Text size="xs" c="dimmed">In Progress</Text>
          </Group>
          <Group gap="xs">
            <Badge variant="filled" color="gray" size="sm">
              <IconMinus size={12} />
            </Badge>
            <Text size="xs" c="dimmed">Not Started</Text>
          </Group>
        </Group>

        {/* Cell Detail Modal */}
        <CellDetailModal
          detail={cellDetail}
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
        />
      </Stack>
    </Container>
  );
}

export default TaskGroupProgressPage;
